import { useState, useCallback } from 'react'
import { parseImageReferences, findImageByReferenceName, getImageReferenceName } from '../utils/imageMentions'
import ImageGallery from './ImageGallery'
import PDFPreviewModal from './PDFPreviewModal'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import './SolutionWithImageReferences.css'

function SolutionWithImageReferences ({ solution, images = [] }) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [pdfModalOpen, setPdfModalOpen] = useState(false)
  const [currentPdf, setCurrentPdf] = useState(null)

  // Open lightbox at specific image index
  const openImageAt = useCallback((imageIndex) => {
    if (imageIndex >= 0 && imageIndex < images.length) {
      setCurrentImageIndex(imageIndex)
      setLightboxOpen(true)
    }
  }, [images.length])

  // Handle click on image reference
  const handleImageReferenceClick = useCallback((referenceName) => {
    // Find the image by reference name (case-insensitive)
    const image = findImageByReferenceName(images, referenceName)
    
    if (image) {
      // Check if it's a PDF
      const isPDF = image.mediaType === 'pdf' || 
                    (image.filename || image.name || '').match(/\.pdf$/i) ||
                    (image.url || '').match(/\.pdf$/i)
      
      if (isPDF) {
        // Open PDF in PDFPreviewModal
        setCurrentPdf({
          filePath: extractStoragePathFromUrl(image.url),
          fileName: image.filename || image.name || 'document.pdf',
          pageNumber: 1
        })
        setPdfModalOpen(true)
      } else {
        // Open image/video in lightbox
        const imageIndex = images.findIndex(img => {
          const imgRefName = img.referenceName || getImageReferenceName(img.filename || img.name || '')
          return imgRefName.toLowerCase() === referenceName.toLowerCase()
        })
        
        if (imageIndex !== -1) {
          openImageAt(imageIndex)
        }
      }
    }
  }, [images, openImageAt])

  // Extract storage path from Firebase Storage URL
  const extractStoragePathFromUrl = (url) => {
    if (!url) return null
    try {
      // Firebase Storage URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media
      const match = url.match(/\/o\/([^?]+)/)
      if (match && match[1]) {
        return decodeURIComponent(match[1])
      }
      // Alternative format: https://storage.googleapis.com/{bucket}/{path}
      const match2 = url.match(/storage\.googleapis\.com\/[^/]+\/(.+?)(?:\?|$)/)
      if (match2 && match2[1]) {
        return decodeURIComponent(match2[1])
      }
    } catch (e) {
      console.error('Error extracting storage path from URL:', e)
    }
    return null
  }

  // Ensure images have referenceName
  const normalizedImages = images.map(img => {
    if (!img.referenceName) {
      return {
        ...img,
        referenceName: getImageReferenceName(img.filename || img.name || '')
      }
    }
    return img
  })

  // Parse solution text for image references
  const segments = parseImageReferences(solution || '')

  return (
    <div className='solution-with-references'>
      <div className='solution-content-text'>
        {segments.map((segment, index) => {
          if (segment.type === 'text') {
            // Render plain text, preserving whitespace
            return (
              <span key={index} className='solution-text-segment'>
                {segment.content}
              </span>
            )
          } else if (segment.type === 'mention') {
            // Check if image exists
            const image = findImageByReferenceName(normalizedImages, segment.referenceName)

            if (image) {
              // Check if it's a PDF for different styling
              const isPDF = image.mediaType === 'pdf' || 
                           (image.filename || image.name || '').match(/\.pdf$/i)
              
              // Valid reference - render as clickable, colored text
              return (
                <button
                  key={index}
                  type='button'
                  className='image-reference-link'
                  onClick={() => handleImageReferenceClick(segment.referenceName)}
                  title={isPDF ? `PDF "${segment.referenceName}" öffnen` : `Bild "${segment.referenceName}" anzeigen`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                  {isPDF && <PictureAsPdf style={{ fontSize: '14px' }} />}
                  {segment.content}
                </button>
              )
            } else {
              // Invalid reference - render as plain text (as specified: normal text, not red)
              return (
                <span key={index} className='solution-text-segment'>
                  {segment.content}
                </span>
              )
            }
          }
          return null
        })}
      </div>

      {/* Hidden ImageGallery for lightbox functionality */}
      {normalizedImages.length > 0 && (
        <>
          <div style={{ display: 'none' }}>
            <ImageGallery
              images={normalizedImages}
              compact={false}
            />
          </div>
          
          {/* Custom lightbox that we control */}
          {lightboxOpen && (
            <div className='image-reference-lightbox-overlay' onClick={() => setLightboxOpen(false)}>
              <div
                className='image-reference-lightbox-content'
                onClick={e => e.stopPropagation()}
              >
                <button
                  className='image-reference-lightbox-close'
                  onClick={() => setLightboxOpen(false)}
                >
                  ✕
                </button>

                {/* Render video or image based on mediaType (PDFs are handled separately) */}
                {normalizedImages[currentImageIndex]?.mediaType === 'video' ? (
                  <video
                    src={normalizedImages[currentImageIndex]?.url}
                    controls
                    style={{ maxWidth: '90vw', maxHeight: '80vh' }}
                  />
                ) : (
                  <img
                    src={normalizedImages[currentImageIndex]?.url}
                    alt={normalizedImages[currentImageIndex]?.referenceName || `Image ${currentImageIndex + 1}`}
                  />
                )}

                <div className='image-reference-lightbox-info'>
                  {normalizedImages[currentImageIndex]?.referenceName && (
                    <span className='image-reference-lightbox-name'>
                      {normalizedImages[currentImageIndex].referenceName}
                    </span>
                  )}
                  <span className='image-reference-lightbox-counter'>
                    {currentImageIndex + 1} / {normalizedImages.length}
                  </span>
                </div>

                {normalizedImages.length > 1 && (
                  <>
                    <button
                      className='image-reference-lightbox-prev'
                      onClick={() => setCurrentImageIndex(prev => (prev - 1 + normalizedImages.length) % normalizedImages.length)}
                    >
                      ‹
                    </button>
                    <button
                      className='image-reference-lightbox-next'
                      onClick={() => setCurrentImageIndex(prev => (prev + 1) % normalizedImages.length)}
                    >
                      ›
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* PDF Preview Modal */}
      {pdfModalOpen && currentPdf && (
        <PDFPreviewModal
          source={currentPdf}
          onClose={() => {
            setPdfModalOpen(false)
            setCurrentPdf(null)
          }}
        />
      )}
    </div>
  )
}

export default SolutionWithImageReferences
